import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import NavBack from 'dashboard/components/NavBack';
import NullState from 'dashboard/components-lib/NullState';
import {
  Backdrop,
  Container,
  FullScreenHeaderContainer,
  StickyContainer,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { SARConfiguration, SARConfigurationAttributes } from 'dashboard/models/sar-configuration';
import { sarOrganizationConfiguration } from 'dashboard/views/DashboardSARConfigurationShowView/SAROganizationConfigurationSchema';
import { transformErrors } from 'dashboard/views/DashboardSARShowView/FormSAR';
import {
  templates,
  widgets,
} from 'dashboard/views/DashboardSARShowView/FormSAR/rjsf/FormComponents';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { TextField } from 'design-system/components/FormField';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';
import { useFormSecurely } from 'lib/security/reactHookForm';

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

interface SARCredentialsFormData {
  sandboxUsername: string;
  sandboxPassword: string;
  productionUsername: string;
  productionPassword: string;
}

export interface DashboardSARConfigurationShowViewProps {
  sarConfiguration?: SARConfiguration;
  updateSARConfiguration: (nextConfiguration: Record<string, any>) => Promise<void>;
  createSARConfiguration: (nextConfiguration: Record<string, any>) => Promise<void>;
}

const DashboardSARConfigurationShowView = ({
  sarConfiguration,
  updateSARConfiguration,
  createSARConfiguration,
}: DashboardSARConfigurationShowViewProps) => {
  // -----------------------------------------------------------------------------------------
  // Massage the server data into the JSON Form Schema.
  // -----------------------------------------------------------------------------------------
  // NB: need to cast this as any so typescript doesn't complain about it being used as formData
  const existingFormData: any = useMemo(
    () => ({
      ...sarConfiguration?.attributes['filing-institution-data'],
    }),
    [sarConfiguration],
  );
  const sarOrganizationConfigurationData = useRef<Record<string, any>>(existingFormData);

  const credentialsByEnvironment = {
    sandbox: sarConfiguration?.attributes['jurisdiction-credentials'].find(
      (credential) => credential.environment === 'sandbox',
    ),
    production: sarConfiguration?.attributes['jurisdiction-credentials'].find(
      (credential) => credential.environment === 'production',
    ),
  };

  const { register, handleSubmit } = useFormSecurely<SARCredentialsFormData>({
    defaultValues: {
      sandboxUsername: credentialsByEnvironment.sandbox?.username,
      sandboxPassword: credentialsByEnvironment.sandbox?.password,
      productionUsername: credentialsByEnvironment.production?.username,
      productionPassword: credentialsByEnvironment.production?.password,
    },
    mode: 'onChange',
  });
  const hasSARConfigurationViewPermission = usePermission(Permission.SARConfigurationView);
  const hasSARConfigurationWritePermission = usePermission(Permission.SARConfigurationWrite);

  const handleUpdateSARConfiguration = (formData: SARCredentialsFormData) => {
    // Massage data from the JSON Form Schema into the shape needed for the request.
    const { filingInstitution, financialInstitutionIdentification } =
      sarOrganizationConfigurationData.current;
    const requestBodyAttributes: Pick<
      SARConfigurationAttributes,
      'filing-institution-data' | 'jurisdiction-credentials'
    > = {
      'filing-institution-data': {},
      'jurisdiction-credentials': [],
    };

    if (filingInstitution && Object.keys(filingInstitution).length > 0) {
      requestBodyAttributes['filing-institution-data']['filingInstitution'] = filingInstitution;
    }

    if (
      financialInstitutionIdentification &&
      Object.keys(financialInstitutionIdentification).length > 0
    ) {
      requestBodyAttributes['filing-institution-data']['financialInstitutionIdentification'] =
        financialInstitutionIdentification;
    }

    if (formData.sandboxUsername && formData.sandboxPassword) {
      requestBodyAttributes['jurisdiction-credentials'].push({
        environment: 'sandbox',
        username: formData.sandboxUsername,
        password: formData.sandboxPassword,
        jurisdiction: 'FinCEN',
      });
    }

    if (formData.productionUsername && formData.productionPassword) {
      requestBodyAttributes['jurisdiction-credentials'].push({
        environment: 'production',
        username: formData.productionUsername,
        password: formData.productionPassword,
        jurisdiction: 'FinCEN',
      });
    }

    if (sarConfiguration) {
      updateSARConfiguration(requestBodyAttributes);
    } else {
      createSARConfiguration(requestBodyAttributes);
    }
  };

  // -----------------------------------------------------------------------------------------
  // Configuration Form component definitions
  // -----------------------------------------------------------------------------------------
  const formRefs = useRef<Record<string, Form<unknown, any> | null>>(
    Object.keys(sarOrganizationConfiguration).reduce(
      (acc, key) => {
        acc[key] = null;
        return acc;
      },
      {} as Record<string, Form<unknown, any> | null>,
    ),
  );

  // NB: sarOrganizationConfigurationData needs to subscribe to updates on existingFormData so the form can be initialized correctly.
  useEffect(() => {
    sarOrganizationConfigurationData.current = existingFormData;
  }, [existingFormData]);

  const subForms = useMemo(
    () =>
      Object.entries(sarOrganizationConfiguration).map(([key, section]) => {
        const { schema, uiSchema } = section;

        return (
          <Form
            key={uuid()} // fixes bug with validations not clearing https://github.com/rjsf-team/react-jsonschema-form/issues/486#issuecomment-282431387
            ref={(element) => {
              formRefs.current[key] = element;
            }}
            schema={schema}
            uiSchema={uiSchema}
            validator={validator}
            onChange={({ formData }) => (sarOrganizationConfigurationData.current[key] = formData)}
            templates={templates}
            widgets={widgets}
            transformErrors={transformErrors}
            showErrorList={false}
            liveValidate={true}
            formData={existingFormData[key]}
          >
            {/* need to pass fragment to get rid of default submit button */}
            <></>
          </Form>
        );
      }),
    [existingFormData],
  );

  const configurationSections = useMemo(
    () =>
      Object.entries(sarOrganizationConfiguration).map(([_, section], index) => {
        const { title } = section;

        return (
          <Card key={index}>
            <Card.Body>
              <Card.Title>{title}</Card.Title>
              {subForms[index]}
            </Card.Body>
          </Card>
        );
      }),
    [subForms],
  );

  if (!hasSARConfigurationViewPermission) {
    return (
      <Backdrop>
        <NullState message='Your role does not have permission to view this page.' />
      </Backdrop>
    );
  }

  return (
    <>
      <StickyContainer>
        <FullScreenHeaderContainer>
          <HorizontalContainer>
            <NavBack to='/dashboard/sars'>SAR Filing Configuration</NavBack>
          </HorizontalContainer>
          <HStack>
            {hasSARConfigurationWritePermission ? (
              <Button
                id='sar-configuration__save'
                variant='basic'
                onClick={handleSubmit(handleUpdateSARConfiguration)}
              >
                Save
              </Button>
            ) : (
              <Tooltip
                trigger={
                  <Button variant='basic' id='sar-configuration__save' disabled>
                    Save
                  </Button>
                }
              >
                You do not have permissions to edit this configuration.
              </Tooltip>
            )}
          </HStack>
        </FullScreenHeaderContainer>
      </StickyContainer>
      <Backdrop>
        <Container>
          {configurationSections}
          <Card>
            <Card.Body>
              <Card.Title>FinCEN credentials</Card.Title>
              <form>
                <Label size='md' weight='bold'>
                  Sandbox
                </Label>
                <HStack>
                  <TextField {...register('sandboxUsername')} label='Username' />
                  <TextField {...register('sandboxPassword')} label='Password' type='password' />
                </HStack>
                <Spacer height={2} />
                <Label size='md' weight='bold'>
                  Production
                </Label>
                <HStack>
                  <TextField {...register('productionUsername')} label='Username' />
                  <TextField {...register('productionPassword')} label='Password' type='password' />
                </HStack>
              </form>
            </Card.Body>
          </Card>
        </Container>
      </Backdrop>
    </>
  );
};

export default DashboardSARConfigurationShowView;
